import PackagingPicker, {
  useAvailableSizes,
} from '@app/ccf/company-report/components/scope-3/packaging/packaging-picker';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPackagingCCFDefaults } from '@shared/api/hooks/ccf/activities/packaging';
import {
  useDeleteSoldPackagingItem,
  usePatchSoldPackagingItem,
} from '@shared/api/hooks/sold-goods/packaging';
import {
  PackagingActivityRetrieve,
  PackagingActivityUpdate,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import InputFormField from '@shared/components/form/input-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { object, string, z } from 'zod';
import { useSoldPackagingItem } from '../../sold-packaging-item-context';

interface SoldPackagingItemFormProps {
  item: PackagingActivityRetrieve;
}

const soldItemFormSchema = object({
  packagingName: string().min(1),
  remoteId: string().optional(),
  packagingType: string().min(1, {
    message: 'Please select a packaging',
  }),
  packagingTypeUuid: string().min(1, {
    message: 'Please select a packaging',
  }),
  itemsPerPack: z.coerce.number().min(0.001),
});

export default function SoldPackagingItemForm({
  item,
}: SoldPackagingItemFormProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { refetchItem, referrer } = useSoldPackagingItem();
  const {
    SimpleDialogComponent: deleteModal,
    closeSimpleDialog: closeDeleteModal,
    showSimpleDialog: showDeleteModal,
  } = useSimpleDialog();
  const { data: packagingOptions, isLoading: packagingOptionsLoading } =
    useGetPackagingCCFDefaults();

  const {
    mutateAsync: updatePackagingActivity,
    isLoading: isUpdatingPackagingActivity,
  } = usePatchSoldPackagingItem();

  const {
    mutateAsync: deletePackagingActivity,
    isLoading: isDeletingPackagingActivity,
  } = useDeleteSoldPackagingItem();

  const form = useForm<z.infer<typeof soldItemFormSchema>>({
    resolver: zodResolver(soldItemFormSchema),
  });
  const { watch, reset } = form;

  const onSubmit = async (data: z.infer<typeof soldItemFormSchema>) => {
    const updatedData = {
      packagingType: data.packagingTypeUuid,
      itemsPerPack: data.itemsPerPack,
    } as PackagingActivityUpdate;

    const importData = updatedData;
    try {
      await updatePackagingActivity({
        activityUuid: item.uuid,
        data: importData,
      });
      await refetchItem();
      toast({
        title: 'Item updated',
        variant: 'success',
        description: 'Packaging activity updated successfully',
      });
    } catch (error) {
      sentry.log(error);
    }
  };

  const handleDelete = async () => {
    try {
      await deletePackagingActivity({ activityUuid: item.uuid });
      toast({
        title: 'Item deleted',
        variant: 'success',
        description: 'Packaging deleted successfully',
      });
      closeDeleteModal();
      referrer
        ? navigate(referrer)
        : navigate(generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK));
    } catch (error) {
      sentry.log(error);
    }
  };

  const watchSelectedPackagingOptionUuid = watch('packagingType');

  const availableSizes = useAvailableSizes(
    packagingOptions!,
    watchSelectedPackagingOptionUuid
  );

  useEffect(() => {
    if (item) {
      reset({
        remoteId: item.remoteId || '',
        packagingName: item.packagingName as string,
        packagingType: item.packagingType?.name ?? '',
        packagingTypeUuid: item.packagingType?.uuid ?? '',
        itemsPerPack: item.itemsPerPack!,
      });
    }
  }, [item, reset]);

  return (
    <Form {...form}>
      <form
        className="min-h-[360px] max-w-form space-y-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <fieldset className="space-y-4" disabled={isUpdatingPackagingActivity}>
          <>
            <div className="grid grid-cols-12 gap-2">
              <InputFormField
                name="remoteId"
                disabled
                label="Your ID"
                className="col-span-3 max-w-fit"
              />
              <InputFormField
                name="packagingName"
                disabled
                label="Packaging name"
                className="col-span-9"
              />
            </div>
            <div>
              <PackagingPicker
                key={watchSelectedPackagingOptionUuid}
                name="packagingType"
                availablePackaging={packagingOptions!}
                className="col-span-6"
                label="Packaging item"
                availableSizes={availableSizes}
                sizeName="packagingTypeUuid"
                isLoading={packagingOptionsLoading}
              />
            </div>
            <div className="grid grid-cols-12 gap-3">
              <InputFormField
                name="itemsPerPack"
                label="Items per pack"
                className="col-span-3"
              />
            </div>
          </>
        </fieldset>

        <div className="mt-12 flex justify-between ">
          <div className="col-span-4">
            <Button
              type="submit"
              disabled={isUpdatingPackagingActivity}
              loading={isUpdatingPackagingActivity}
            >
              Update sold item
            </Button>
          </div>
          <div className="col-span-4">
            <Button
              type="button"
              variant="destructive"
              onClick={showDeleteModal}
              loading={isDeletingPackagingActivity}
              disabled={isUpdatingPackagingActivity}
            >
              Delete item
            </Button>
          </div>
          {deleteModal({
            header: 'Delete sold item',
            content: (
              <Text>
                Deleting this item will also remove all related{' '}
                <TextLink
                  to={generatePath(soldGoodsRoutes.SOLD_PACKAGING_ITEM_SALES, {
                    item_uuid: item.uuid,
                  })}
                >
                  sales
                </TextLink>{' '}
                from your reports. This cannot be undone.
              </Text>
            ),
            footer: (
              <div className="space-x-2">
                <Button
                  variant="outline"
                  onClick={closeDeleteModal}
                  disabled={isDeletingPackagingActivity}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  loading={isDeletingPackagingActivity}
                >
                  Delete item
                </Button>
              </div>
            ),
          })}
        </div>
        <Text variant="subtle">
          Updating the modelling for this item will update all related{' '}
          <TextLink
            to={generatePath(soldGoodsRoutes.SOLD_PACKAGING_ITEM_SALES, {
              item_uuid: item.uuid,
            })}
          >
            sales
          </TextLink>
          , all reports will need to be republished.
        </Text>
      </form>
    </Form>
  );
}
